import React from "react";
import { useQuery } from "@tanstack/react-query";
import { endpoints } from "@/api";
import { useI18n } from "@/i18n";
import { resolveAssetUrl } from "@/components/ImageUploader";
import Seo from "@/components/Seo";

// Fallback image used when a stage has no image set in the DB.
const FALLBACK_IMG = "https://images.unsplash.com/photo-1495474472287-4d71bcdd2085?auto=format&fit=crop&w=940&q=80";

export default function Journey() {
  const { t, isRTL, lang } = useI18n();
  const serifClass = isRTL ? "font-serif-ar" : "font-serif-en";

  const { data: journey } = useQuery({
    queryKey: ["journey"],
    queryFn: endpoints.journey,
  });

  // Prefer CMS data; fall back to i18n bundle while loading or if empty.
  const headTitle =
    (lang === "ar" ? journey?.title_ar : journey?.title_en) || t.journey.title;
  const headLead =
    (lang === "ar" ? journey?.lead_ar : journey?.lead_en) || t.journey.lead;
  const stages = (journey?.stages && journey.stages.length > 0)
    ? journey.stages.map((s) => ({
        id: s.id,
        tag: (lang === "ar" ? s.tag_ar : s.tag_en) || s.tag_en || s.tag_ar || "",
        title: (lang === "ar" ? s.title_ar : s.title_en) || s.title_en || s.title_ar || "",
        body: (lang === "ar" ? s.body_ar : s.body_en) || s.body_en || s.body_ar || "",
        image: s.image || FALLBACK_IMG,
      }))
    : t.journey.stages.map((s, i) => ({ id: `i18n-${i}`, ...s, image: FALLBACK_IMG }));

  return (
    <div className="max-w-6xl mx-auto px-6 lg:px-10 pt-10 lg:pt-14 pb-20" data-testid="journey-page">
      <Seo
        title={isRTL ? "رحلة القهوة — من البذرة إلى الفنجان" : "Coffee Journey — Seed to Cup"}
        description={isRTL
          ? "رحلة القهوة الكاملة بالعربية: الزراعة، الحصاد، المعالجة، التحميص، التحضير. خمس مراحل تشرح كل ما يحدث للبذرة قبل أن تصبح في فنجانك."
          : "The full specialty coffee journey: growing, harvesting, processing, roasting and brewing — explained step by step."}
        keywords="رحلة القهوة, من البذرة إلى الفنجان, زراعة القهوة, معالجة القهوة, حصاد القهوة, مراحل القهوة"
        path="/journey"
      />
      <div className="max-w-3xl">
        <div className="text-[10px] uppercase tracking-[0.3em] text-[#C86240] mb-3">
          {isRTL ? "خمس مراحل" : "Five stages"}
        </div>
        <h1 className={`${serifClass} text-3xl lg:text-5xl text-[#2A2421] leading-[1.05]`}>{headTitle}</h1>
        <p className="mt-4 text-[#5C544F] leading-relaxed">{headLead}</p>
      </div>

      <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {stages.map((stage, idx) => (
          <article
            key={stage.id}
            data-testid={`journey-stage-${idx}`}
            className="group rounded-2xl bg-white border border-[#2A2421]/10 overflow-hidden hover:border-[#C86240]/40 hover:-translate-y-0.5 transition-all"
          >
            <div className="relative aspect-[16/10] overflow-hidden">
              <img
                src={resolveAssetUrl(stage.image)}
                alt=""
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                loading="lazy"
              />
              <div className="absolute top-3 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/95 text-[10px] font-semibold text-[#2A2421] tabular-nums shadow-sm"
                style={isRTL ? { right: "12px" } : { left: "12px" }}
              >
                <span className="text-[#C86240]">{String(idx + 1).padStart(2, "0")}</span>
                <span className="opacity-50">·</span>
                <span className="uppercase tracking-wider">{stage.tag}</span>
              </div>
            </div>
            <div className="p-4">
              <h2 className={`${serifClass} text-lg text-[#2A2421] leading-snug`}>{stage.title}</h2>
              <p className="mt-2 text-sm text-[#5C544F] leading-relaxed line-clamp-4">{stage.body}</p>
            </div>
          </article>
        ))}
      </div>
    </div>
  );
}
